/**
 * ``useFirstPaintReflow`` — force a synchronous layout/paint on first mount.
 *
 * Telegram Mini-App webviews (and iOS Safari) sometimes report a 0-height viewport on
 * the very first layout pass. Layouts sized from the viewport (``100vh`` / ``100dvh``)
 * then collapse and the page renders blank until *some* event (often a 1px resize)
 * forces a reflow and the content suddenly appears.
 *
 * This hook eliminates that wait: it reads a layout property in ``useLayoutEffect``
 * (forcing a synchronous reflow before the browser paints), then re-nudges on the next
 * animation frame to cover the case where the webview only reports the real viewport
 * height a frame later. It is a no-op outside the browser (SSR / tests without a DOM).
 */
import { useLayoutEffect } from "react";

/** Read a layout-dependent property to force the engine to flush layout. */
function forceReflow(): void {
  if (typeof document === "undefined" || document.documentElement === null) {
    return;
  }
  // Accessing offsetHeight forces a synchronous reflow (the value is intentionally
  // unused). ``void`` keeps strict/no-unused-expressions linters happy.
  void document.documentElement.offsetHeight;
}

export function useFirstPaintReflow(): void {
  useLayoutEffect(() => {
    // 1) Synchronous reflow before the first paint.
    forceReflow();

    if (typeof window === "undefined") {
      return;
    }

    // 2) Re-nudge after the first frame: some webviews only settle the viewport
    //    height one frame in. Reflow again so a still-collapsed layout expands.
    const raf = window.requestAnimationFrame(() => {
      forceReflow();
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, []);
}
